import { useState, useCallback, useEffect } from "react";
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { type Address, formatUnits } from "viem";
import { ADDRESSES, JUMP_FUD_ABI, JUMP_FUD_TOKEN_ABI } from "../config/contracts";
import { type MemeToken, type TokenStats, TokenStatus } from "../config/tokens";
import { parseTokenAmount, getDeadline, calculateSlippage } from "../lib/utils";
import { useTokenAllowance } from "./useTokenAllowance";

export function useLaunchpadConfig() {
  const { data: tokenCount } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "tokenCount",
    query: { refetchInterval: 15_000 },
  });

  const { data: launchFee } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "launchFee",
    query: { staleTime: 60_000 },
  });

  const { data: buyFeeBps } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "buyFeeBps",
    query: { staleTime: 60_000 },
  });

  const { data: sellFeeBps } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "sellFeeBps",
    query: { staleTime: 60_000 },
  });

  const { data: graduationThreshold } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "graduationThreshold",
    query: { staleTime: 60_000 },
  });

  return {
    tokenCount: (tokenCount as bigint) ?? 0n,
    launchFee: (launchFee as bigint) ?? 0n,
    buyFeeBps: (buyFeeBps as bigint) ?? 0n,
    sellFeeBps: (sellFeeBps as bigint) ?? 0n,
    graduationThreshold: (graduationThreshold as bigint) ?? 0n,
  };
}

export function useLaunchpadTokens(offset: bigint = 0n, limit: bigint = 50n) {
  const { data: allTokens, isLoading, refetch } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "getAllTokens",
    args: [offset, limit],
    query: { refetchInterval: 15_000 },
  });

  const { data: liveTokens, isLoading: isLoadingLive } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "getLiveTokens",
    args: [offset, limit],
    query: { refetchInterval: 15_000 },
  });

  const tokens = (allTokens as MemeToken[]) ?? [];
  const live = (liveTokens as MemeToken[]) ?? [];

  return {
    tokens,
    liveTokens: live,
    isLoading: isLoading || isLoadingLive,
    refetch,
  };
}

export function useTokenData(tokenId: bigint | undefined) {
  const { data: tokenData, isLoading: isLoadingToken, refetch: refetchToken } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "getToken",
    args: tokenId !== undefined ? [tokenId] : undefined,
    query: {
      enabled: tokenId !== undefined,
      refetchInterval: 10_000,
    },
  });

  const { data: statsData, isLoading: isLoadingStats, refetch: refetchStats } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "getTokenStats",
    args: tokenId !== undefined ? [tokenId] : undefined,
    query: {
      enabled: tokenId !== undefined,
      refetchInterval: 10_000,
    },
  });

  const token = tokenData as MemeToken | undefined;
  const rawStats = statsData as [bigint, bigint, bigint, bigint, bigint, number] | undefined;
  const stats: TokenStats | undefined = rawStats
    ? {
        price: rawStats[0],
        marketCap: rawStats[1],
        reserveBalance: rawStats[2],
        tokensSold: rawStats[3],
        progress: rawStats[4],
        status: rawStats[5] as TokenStatus,
      }
    : undefined;

  return {
    token,
    stats,
    isLoading: isLoadingToken || isLoadingStats,
    refetch: () => {
      refetchToken();
      refetchStats();
    },
  };
}

export function useTokenDataByAddress(tokenAddress: Address | undefined) {
  const { data: tokenId } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "tokenToId",
    args: tokenAddress ? [tokenAddress] : undefined,
    query: {
      enabled: !!tokenAddress,
      staleTime: Infinity,
    },
  });

  const id = tokenId as bigint | undefined;
  return useTokenData(id);
}

export function useLaunchToken() {
  const { address: account } = useAccount();
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();
  const { launchFee } = useLaunchpadConfig();

  const { writeContract, isPending } = useWriteContract();

  const { isLoading: isConfirming, isSuccess, data: receipt } =
    useWaitForTransactionReceipt({ hash: txHash });

  const launch = useCallback(
    (name: string, symbol: string, description: string, imageUri: string, initialBuyAmount: bigint = 0n) => {
      if (!account) return;

      const value = launchFee + initialBuyAmount;

      writeContract(
        {
          address: ADDRESSES.JUMP_FUD,
          abi: JUMP_FUD_ABI,
          functionName: "launchToken",
          args: [name, symbol, description, imageUri],
          value,
        },
        { onSuccess: (hash) => setTxHash(hash) }
      );
    },
    [account, launchFee, writeContract]
  );

  return {
    launch,
    isPending,
    isConfirming,
    isSuccess,
    txHash,
    receipt,
  };
}

export function useBuyTokens(tokenId: bigint | undefined, slippageBps: number = 100) {
  const { address: account } = useAccount();
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();
  const [buyAmount, setBuyAmount] = useState("");

  const parsedAmount = parseTokenAmount(buyAmount);

  const { data: quotedTokensOut } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "calculateBuyAmount",
    args: tokenId !== undefined ? [tokenId, parsedAmount] : undefined,
    query: {
      enabled: tokenId !== undefined && parsedAmount > 0n,
      refetchInterval: 10_000,
    },
  });

  const { writeContract, isPending } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  useEffect(() => {
    if (isSuccess) {
      setBuyAmount("");
      setTxHash(undefined);
    }
  }, [isSuccess]);

  const executeBuy = useCallback(() => {
    if (!account || tokenId === undefined || parsedAmount === 0n) return;

    const minOut = quotedTokensOut
      ? calculateSlippage(quotedTokensOut as bigint, slippageBps)
      : 0n;

    writeContract(
      {
        address: ADDRESSES.JUMP_FUD,
        abi: JUMP_FUD_ABI,
        functionName: "buyTokens",
        args: [tokenId, minOut],
        value: parsedAmount,
      },
      { onSuccess: (hash) => setTxHash(hash) }
    );
  }, [account, tokenId, parsedAmount, quotedTokensOut, slippageBps, writeContract]);

  return {
    buyAmount,
    setBuyAmount,
    quotedTokensOut: (quotedTokensOut as bigint) ?? 0n,
    executeBuy,
    isPending,
    isConfirming,
    isSuccess,
    txHash,
  };
}

export function useSellTokens(
  tokenId: bigint | undefined,
  tokenAddress: Address | undefined,
  slippageBps: number = 100
) {
  const { address: account } = useAccount();
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();
  const [sellAmount, setSellAmount] = useState("");

  const parsedAmount = parseTokenAmount(sellAmount);

  const { data: quotedJasmyOut } = useReadContract({
    address: ADDRESSES.JUMP_FUD,
    abi: JUMP_FUD_ABI,
    functionName: "calculateSellAmount",
    args: tokenId !== undefined ? [tokenId, parsedAmount] : undefined,
    query: {
      enabled: tokenId !== undefined && parsedAmount > 0n,
      refetchInterval: 10_000,
    },
  });

  const { needsApproval, approve, isApproving } = useTokenAllowance(
    tokenAddress,
    ADDRESSES.JUMP_FUD,
    parsedAmount
  );

  const { writeContract, isPending } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  useEffect(() => {
    if (isSuccess) {
      setSellAmount("");
      setTxHash(undefined);
    }
  }, [isSuccess]);

  const executeSell = useCallback(() => {
    if (!account || tokenId === undefined || parsedAmount === 0n) return;

    const minOut = quotedJasmyOut
      ? calculateSlippage(quotedJasmyOut as bigint, slippageBps)
      : 0n;

    writeContract(
      {
        address: ADDRESSES.JUMP_FUD,
        abi: JUMP_FUD_ABI,
        functionName: "sellTokens",
        args: [tokenId, parsedAmount, minOut],
      },
      { onSuccess: (hash) => setTxHash(hash) }
    );
  }, [account, tokenId, parsedAmount, quotedJasmyOut, slippageBps, writeContract]);

  return {
    sellAmount,
    setSellAmount,
    quotedJasmyOut: (quotedJasmyOut as bigint) ?? 0n,
    needsApproval,
    approve,
    isApproving,
    executeSell,
    isPending,
    isConfirming,
    isSuccess,
    txHash,
  };
}
